import { useEffect, useState } from 'react'

/**
 * A clock that ticks once, when a run's state is next due to change.
 *
 * Needed because the stalled rule is the one piece of run state that time alone
 * decides. A killed job stops writing, so its entry stops changing, so SWR keeps
 * handing back the same data — exactly the case where a spinner would otherwise
 * spin forever. One timer at the transition, rather than a polling interval,
 * means an entry that is already stalled or already finished schedules nothing.
 */
export const useRunStateClock = (transitionAt: number | null): number => {
  const [now, setNow] = useState(() => Date.now())

  useEffect(() => {
    if (transitionAt === null) return
    // A tick that has come due while we were not watching still has to land, so
    // clamp rather than skip; a second of slack keeps the comparison off the
    // exact boundary.
    const delay = Math.max(0, transitionAt - Date.now()) + 1_000
    const timer = window.setTimeout(() => setNow(Date.now()), delay)
    return () => window.clearTimeout(timer)
  }, [transitionAt])

  return now
}
